/* Header — Liquid Glass: class-toggle menu (mobile) + per-link mega menu.
   Mobile: the three-dot mark toggles .is-menu-open; tab switch via nav-link tap.
   Desktop: hover with 500ms hide delay; the mark is inert so the logo→home tap
   and the hover-mega stay clean.
   Paired with sections/header-liquid-glass.liquid + section-header-liquid-glass.css */
(() => {
  const MEGA_HIDE_DELAY = 500;
  const desktopMq = window.matchMedia('(min-width: 750px)');

  if (customElements.get('header-liquid-glass')) return;

  class HeaderLiquidGlass extends HTMLElement {
    connectedCallback() {
      if (this._init) return;          // guard: shopify:section:load + native attach can both fire
      this._init = true;

      // Bind once so add/remove on document target the same reference.
      this._doc = this._doc.bind(this);
      this._key = this._key.bind(this);

      // Cache the nodes we touch repeatedly.
      this._menus = new Map();
      this.querySelectorAll('.hglass__menu[data-mega-id]')
        .forEach(m => this._menus.set(m.dataset.megaId, m));
      this._megaLinks = Array.from(this.querySelectorAll('.hglass__nav-link[data-mega-id]'));

      // Remember which mega was server-rendered as the default tab so we can
      // restore selection when the mobile menu closes and reopens.
      const initial = this.querySelector('.hglass__menu.is-selected');
      this._defaultMegaId = initial ? initial.dataset.megaId : null;

      // Menu triggers: the three-dot mark opens, the ✕ closes — both carry
      // data-hglass-menu-toggle. Mobile-only: on desktop the menu is hover-driven
      // and the mark is inert, so the logo→home tap and hover-mega stay clean.
      // stopPropagation keeps the opening click from reaching the document
      // dismiss handler (which would immediately re-close it).
      this._menuToggles = Array.from(this.querySelectorAll('[data-hglass-menu-toggle]'));
      this._menuToggles.forEach(btn => btn.addEventListener('click', (e) => {
        if (desktopMq.matches) return;
        e.stopPropagation();
        this._toggleMenu();
      }));

      this.addEventListener('click', this._click.bind(this));

      // Search: trigger button inside the actions pill opens a sibling glass
      // panel. Click must NOT toggle the menu — defensive stopPropagation.
      this._searchTrigger = this.querySelector('[data-hglass-search-toggle]');
      this._searchPanel = this.querySelector('.hglass__search-panel');
      this._searchInput = this.querySelector('.hglass__search-input');
      if (this._searchTrigger && this._searchPanel) {
        this._searchTrigger.addEventListener('click', (e) => {
          e.stopPropagation();
          this._toggleSearch();
        });
        const closeBtn = this.querySelector('[data-hglass-search-close]');
        closeBtn?.addEventListener('click', (e) => {
          e.stopPropagation();
          this._closeSearch();
        });
      }

      // Persistent document listeners — no-op when nothing's open. Cheaper than
      // toggling add/remove every state change, and handles all dismiss paths.
      document.addEventListener('click', this._doc);
      document.addEventListener('keydown', this._key);

      // Delegated hover/focus: one pair of listeners on the host instead of
      // four on every link + every menu. mouseover/out bubble; focusin/out too.
      this.addEventListener('mouseover', this._over.bind(this));
      this.addEventListener('mouseout', this._out.bind(this));
      this.addEventListener('focusin', this._over.bind(this));
      this.addEventListener('focusout', this._out.bind(this));

      // Scroll morph: passive scroll listener reads window.scrollY directly.
      this._onScroll = () => {
        this.classList.toggle('is-scrolled', window.scrollY > 0);
      };
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._onScroll();

      // Width sync: the mega card mirrors the brand pill's outer width — the
      // brand pill is the menu surface on BOTH breakpoints now (desktop:
      // content-sized with inline nav; mobile: grows to fill the row on open),
      // so the mega's right edge aligns with it. ResizeObserver fires on font
      // load, logo decode, menu open/close (brand grows), and viewport resize.
      this._brandPill = this.querySelector('.hglass__pill--brand');
      this._syncMegaWidth = () => {
        if (!this._brandPill || !this._brandPill.offsetWidth) return;
        const w = `${this._brandPill.offsetWidth}px`;
        this._menus.forEach(menu => menu.style.width = w);
      };
      this._pillRO = new ResizeObserver(() => this._syncMegaWidth());
      if (this._brandPill) this._pillRO.observe(this._brandPill);

      // Scroll containment: preventDefault only when the mega has nothing to
      // scroll (one item → scrollHeight == clientHeight), so wheel/touch don't
      // chain to the page. Native overscroll-behavior handles the overflow case.
      this._megaScrollGuard = (e) => {
        const menu = e.target.closest?.('.hglass__menu');
        if (!menu) return;
        const body = menu.querySelector('.hglass__menu-body');
        if (!body) return;
        if (body.scrollHeight <= body.clientHeight) e.preventDefault();
      };
      this.addEventListener('wheel', this._megaScrollGuard, { passive: false });
      this.addEventListener('touchmove', this._megaScrollGuard, { passive: false });
    }
    disconnectedCallback() {
      if (this._doc) document.removeEventListener('click', this._doc);
      if (this._key) document.removeEventListener('keydown', this._key);
      if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
      this._pillRO?.disconnect();
      clearTimeout(this._megaHideTimer);
    }
    get _menuOpen() { return this.classList.contains('is-menu-open'); }
    _toggleMenu() { this._menuOpen ? this._closeMenu() : this._openMenu(); }
    _openMenu() {
      // Menu and search are mutually exclusive.
      this._closeSearch();
      this.classList.add('is-menu-open');
      this._menuToggles.forEach(b => b.setAttribute('aria-expanded', 'true'));
    }
    _closeMenu() {
      if (!this._menuOpen) return;
      this.classList.remove('is-menu-open');
      this._menuToggles.forEach(b => b.setAttribute('aria-expanded', 'false'));
      // Restore the merchant-configured default tab for the next open.
      if (this._defaultMegaId) this._selectTab(this._defaultMegaId);
    }
    _click(e) {
      // The toggle buttons own their own clicks (handled above, with stopPropagation).
      if (e.target.closest('[data-hglass-menu-toggle]')) return;
      const navLink = e.target.closest('a.hglass__nav-link');
      if (!navLink) return;                              // featured/CTA: let browser navigate
      if (navLink.dataset.megaId && !desktopMq.matches) {
        // Mobile tap on a tab link: switch tab, don't navigate, don't close.
        e.preventDefault();
        this._selectTab(navLink.dataset.megaId);
        return;
      }
      // No mega → standard nav link. Close menu + let browser navigate.
      this._closeMenu();
      this._hideAllMegas();
    }
    _selectTab(megaId) {
      for (const link of this._megaLinks) {
        const match = link.dataset.megaId === megaId;
        link.classList.toggle('is-selected', match);
        link.setAttribute('aria-selected', match ? 'true' : 'false');
      }
      this._menus.forEach((menu, id) => menu.classList.toggle('is-selected', id === megaId));
    }
    _doc(e) {
      if (this.contains(e.target)) return;
      if (this._menuOpen) this._closeMenu();
      if (this.classList.contains('is-search-open')) this._closeSearch();
    }
    _key(e) {
      if (e.key !== 'Escape') return;
      if (this._menuOpen) this._closeMenu();
      this._hideAllMegas();
      if (this.classList.contains('is-search-open')) this._closeSearch();
    }
    _toggleSearch() {
      this.classList.contains('is-search-open') ? this._closeSearch() : this._openSearch();
    }
    _openSearch() {
      // Mutually exclusive with the menu.
      this._closeMenu();
      this._hideAllMegas();
      this.classList.add('is-search-open');
      this._searchTrigger?.setAttribute('aria-expanded', 'true');
      // Focus after layout so iOS soft-keyboard slides in cleanly.
      requestAnimationFrame(() => this._searchInput?.focus());
    }
    _closeSearch() {
      if (!this.classList.contains('is-search-open')) return;
      this.classList.remove('is-search-open');
      this._searchTrigger?.setAttribute('aria-expanded', 'false');
    }
    _over(e) {
      if (!desktopMq.matches) return;                    // desktop hover only
      const trigger = e.target.closest?.('[data-mega-id]');
      const id = trigger?.dataset.megaId;
      if (id && this._menus.has(id)) {
        clearTimeout(this._megaHideTimer);
        this._menus.forEach((m, mid) => m.classList.toggle('is-active', mid === id));
      }
    }
    _out(e) {
      if (!desktopMq.matches) return;
      if (e.target.closest?.('[data-mega-id]')) this._scheduleHide();
    }
    _scheduleHide() {
      clearTimeout(this._megaHideTimer);
      this._megaHideTimer = setTimeout(() => this._hideAllMegas(), MEGA_HIDE_DELAY);
    }
    _hideAllMegas() {
      this._menus?.forEach(menu => menu.classList.remove('is-active'));
    }
  }
  customElements.define('header-liquid-glass', HeaderLiquidGlass);
})();
